<template>
  <HttpFormView :endpoint="rule ? utilities.path('modules/automation/rules/@', rule.id) : utilities.path('communities/@/modules/automation/rules', communityId)" :method="!rule ? 'post' : 'put'" :content="!rule ? { active: true } : rule" @success="emit('success')">
    <template v-slot:form="{ content, errors, submit, loading }">
      <div class="d-flex flex-column g-4">
        <InputView type="text" v-model:value="content.name" :max="32" :title="translate('name')" :errors="errors.name" required/>
        <CheckboxInputView :title="translate('active')" color="primary" v-model:value="content.active"/>
        <div v-if="config" class="d-flex flex-column g-4">
          <div>
            <h4 class="text-white mb-3">{{ translate("automation.when_this_happens") }}</h4>
            <TargetedModalView>
              <template v-slot:target="{ show }">
                <div class="bg-secondary clickable p-4 rounded-4" :class="{ 'border border-1 border-danger': errors.triggerKey != null }" @click="show">
                  <div v-if="selectedTrigger">
                    <div class="d-flex align-items-center justify-content-between">
                      <div class="d-flex align-items-center">
                        <i :class="selectedTrigger.icon" class="fa-lg"></i>
                        <span class="ms-3">{{ selectedTrigger.title }}</span>
                      </div>
                      <div v-if="selectedTrigger.module" class="rounded-3 bg-secondary fw-bold p-2 small" style="word-break: keep-all;"><i :class="selectedTrigger.module.icon" class="fa-fw me-1"></i> {{ selectedTrigger.module.name }}</div>
                    </div>
                    <InputErrorsView v-if="errors.triggerKey && errors.triggerKey.length > 0" class="mt-3" :errors="errors.triggerKey"/>
                  </div>
                  <div v-else class="text-light d-flex align-items-center">
                    <i class="fas fa-play-circle fa-lg text-primary"></i>
                    <span class="ms-3">{{ translate("automation.trigger") }}</span>
                  </div>
                </div>
              </template>
              <template v-slot:body="{ hide }">
                <div class="d-flex align-items-center justify-content-between mb-4">
                  <h3 class="mb-0">{{ translate("automation.trigger") }}</h3>
                  <ModalCloseButtonView @click="hide"/>
                </div>
                <AutomationRuleTriggerForm :trigger="selectedTrigger?.key" :triggers="config.triggers" @update:trigger="updateTrigger(content, $event); hide();"/>
              </template>
            </TargetedModalView>
          </div>
          <div v-if="selectedTrigger">
            <h4 class="text-white mb-3">{{ translate("automation.if_conditions_match") }}</h4>
            <TargetedModalView size="large">
              <template v-slot:target="{ show }">
                <div class="bg-secondary clickable p-4 rounded-4" :class="{ 'border border-1 border-danger': errors.conditions != null }" @click="show">
                  <div class="text-light d-flex align-items-center">
                    <i class="fas fa-filter fa-lg text-info"></i>
                    <span class="ms-3">{{ translate("automation.conditions") }}</span>
                  </div>
                </div>
              </template>
              <template v-slot:body="{ hide }">
                <AutomationRuleConditionsForm v-if="selectedTrigger" :community-id="communityId" :conditions="content.conditions" :all-conditions="config.conditions" @update:conditions="content.conditions = $event.length > 0 ? $event : undefined" @close="hide" :trigger="selectedTrigger" :data-types="config.dataTypes" :errors="errors.conditions"/>
              </template>
            </TargetedModalView>
          </div>
          <div v-if="selectedTrigger">
            <h4 class="text-white mb-3">{{ translate("automation.do_the_following") }}</h4>
            <ActionsForm :community-id="communityId" :value="content.actions" :trigger="selectedTrigger" :data-types="config.dataTypes" :actions="config.actions" @update:value="content.actions = $event" :errors="errors.actions"/>
          </div>
        </div>
        <div class="d-flex align-items-center g-4">
          <ButtonView type="normal" color="primary" class="w-100" @click="submit" :disabled="!content.name || !content.triggerKey || !content.actions || !content.actions.length === 0">
            {{ translate("save") }}
          </ButtonView>
          <ButtonView type="normal" color="secondary" class="w-100" @click="emit('cancel')">
            {{ translate("cancel") }}
          </ButtonView>
        </div>
        <SpinnerView :active="loading" color="primary"/>
      </div>
    </template>
  </HttpFormView>
</template>

<script setup>
import {HttpFormView, ModalCloseButtonView, CheckboxInputView, InputView, ButtonView, SpinnerView, InputErrorsView, utilities, toasts, TargetedModalView} from "@/niftybit";
import AutomationRuleTriggerForm from "@/systems/modules/automation/form/AutomationRuleTriggerForm.vue";
import AutomationRuleConditionsForm from "@/systems/modules/automation/form/conditions/AutomationRuleConditionsForm.vue";
import {onMounted, ref} from "vue";
import axios from "axios";
import ActionsForm from "@/systems/modules/automation/form/actions/ActionsForm.vue";

const props = defineProps({
  communityId: String,
  rule: Object
});

const emit = defineEmits(["success", "cancel"]);


const config = ref(undefined);
const selectedTrigger = ref(undefined);

onMounted(() => {
  axios.get(utilities.path("communities/@/modules/automation/config", props.communityId)).then(response => {
    config.value = response.data;
    if (config.value) {
      const triggerKey = props.rule?.triggerKey ?? props.rule?.trigger?.key;
      selectedTrigger.value = triggerKey ? config.value?.triggers?.find(item => item.key === triggerKey) : undefined;
    }
  }).catch(error => {
    toasts.create({
      color: "danger",
      content: "Failed to load automation configuration. (" + error.message + ")"
    });
  });
});

function updateTrigger(form, key) {
  selectedTrigger.value = key ? config.value?.triggers?.find(item => item.key === key) : undefined;
  form.triggerKey = key;
  form.conditions = undefined;
}

</script>